import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../store";
import { sendMessageToAgent } from "../../store/userSlice";
import { v4 as uuid } from "uuid";
import ResultsStep from "../../pages/SkillGapAssessment/components/ResultsStep";
import type { SubmitResponse } from "../../pages/SkillGapAssessment/types";
import axiosClient from "../../api/AxiosClient";
import styles from "./chatwindow.module.css";

export default function LearningPathButton(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessmentData, setAssessmentData] = useState<SubmitResponse | null>(
    null,
  );
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  // Get current agent and session info from Redux
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("sessionId");
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  const handleGenerateLearningPath = async () => {
    setLoading(true);
    setError(null);
    setNoDataAvailable(false);

    try {
      const response = await axiosClient.get<SubmitResponse>(
        "/skill-gap-information",
      );

      // Check if response has valid data
      const hasValidData =
        response.data &&
        ((response.data.skills && response.data.skills.length > 0) ||
          (response.data.result?.data?.skills &&
            response.data.result.data.skills.length > 0));

      if (!hasValidData) {
        setNoDataAvailable(true);
        setAssessmentData(null);
      } else {
        setAssessmentData(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching skill gap information:", err);

      // Check if it's a 404 or no data error
      if (
        err.response?.status === 404 ||
        err.response?.data?.message?.includes("not found")
      ) {
        setNoDataAvailable(true);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to fetch skill gap assessment data. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAssessmentData(null);
    setError(null);
  };

  const handleProceed = () => {
    if (!currentAgent) return;

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload: any = {
      agentId: currentAgent.id,
      message: "proceed",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    dispatch(sendMessageToAgent(payload));
  };

  const handleNavigateToAssessment = () => {
    navigate("/skill-gap-assessment");
  };

  return (
    <Box className={styles.learningPathContainer}>
      {/* Error display */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, mx: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* No data available message */}
      {noDataAvailable && (
        <Box className={styles.learningPathNoDataWrapper}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Please complete the skill gap assessment and return here to
              generate the learning path.
            </Typography>
          </Alert>
          <Box className={styles.learningPathButtonWrapper}>
            <Button
              variant="contained"
              onClick={handleNavigateToAssessment}
              className={styles.learningPathButton}
            >
              Go to Skill Gap Assessment
            </Button>
          </Box>
        </Box>
      )}

      {/* Generate button - shown when no assessment data is loaded */}
      {!assessmentData && !noDataAvailable && (
        <Box className={styles.learningPathButtonWrapper}>
          <Button
            variant="contained"
            onClick={handleGenerateLearningPath}
            disabled={loading}
            className={styles.learningPathButton}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#004141" }} />
                Loading Assessment Data...
              </>
            ) : (
              "Generate Learning Path"
            )}
          </Button>
        </Box>
      )}

      {/* Results display - shown after data is fetched */}
      {assessmentData && (
        <Box className={styles.learningPathResults}>
          <ResultsStep
            result={assessmentData}
            onReset={handleReset}
            hideActions={true}
          />

          {/* Proceed button to continue workflow */}
          <Box className={styles.learningPathProceedWrapper}>
            <Button
              variant="contained"
              onClick={handleProceed}
              disabled={agentLoading}
              className={styles.learningPathProceedButton}
            >
              {agentLoading ? (
                <>
                  <CircularProgress
                    size={20}
                    sx={{ mr: 1, color: "#004141" }}
                  />
                  Processing...
                </>
              ) : (
                "Proceed"
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
